import { Link } from "react-router-dom";
import { Instagram, MapPin, Clock, Mail, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { PHONE_HREF, WHATSAPP_HREF, sendEvent } from "@/lib/tracking";

// Footer refined per 2026-02 brief:
//   - Nav trimmed to the five conversion-relevant links.
//   - Newsletter form removed (Mailchimp integration not yet verified).
//   - Empty Facebook / YouTube icon links removed to avoid dead endpoints.
const NAV_LINKS = [
  { to: "/group-personal-training-windermere", label: "Group Coaching" },
  { to: "/gym-windermere", label: "Gym Membership" },
  { to: "/30-day-trial", label: "£99 30-Day Trial" },
  { to: "/classes", label: "Classes" },
  { to: "/womens-strength-foundations", label: "Women's Strength Foundations" },
  { to: "/fuel-chapters", label: "Fuel Chapters" },
  { to: "/studio-hire-windermere", label: "Hire Our Space" },
  { to: "/about", label: "About KORE" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-kore-forest text-kore-warm">
      <div className="container-kore py-20 grid grid-cols-1 md:grid-cols-3 gap-12">
        <div className="max-w-md">
          <div className="text-kore-light">
            <Logo size="lg" asLink={false} />
          </div>
          <p className="mt-6 text-base text-kore-warm/75 leading-[1.6] font-light">
            The gym for people 40+ who want to get fit, stay fit and enjoy doing it. Rowan House, Bowness-on-Windermere, Cumbria.
          </p>
        </div>

        <div>
          <div className="eyebrow mb-5">Visit</div>
          <ul className="space-y-4 text-base text-kore-warm/80 leading-[1.6] font-light">
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-1 text-kore-light shrink-0" />
              <span>
                Kore Wellbeing Ltd<br/>
                Rowan House, Beresford Road<br/>
                Bowness-on-Windermere, LA23 2JG
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={16} className="mt-1 text-kore-light shrink-0" />
              <span>
                Open gym: Mon &amp; Wed 09:00 – 18:00<br/>
                Tue &amp; Fri 09:00 – 14:00 · Thu 11:00 – 18:00<br/>
                Sat 08:30 – 14:00 · Sun 08:30 – 12:00<br/>
                Group coaching weekdays from 06:00
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="mt-1 text-kore-light shrink-0" />
              <a
                href="mailto:info@korewellbeing.com"
                className="hover:text-kore-light transition"
                data-testid="footer-email"
              >
                info@korewellbeing.com
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={16} className="mt-1 text-kore-light shrink-0" />
              <span className="flex flex-col gap-1">
                <a href={PHONE_HREF} data-testid="footer-phone" onClick={() => sendEvent("phone_click", { label: "footer" })} className="hover:text-kore-light transition">07429 565591</a>
                <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" onClick={() => sendEvent("whatsapp_click", { label: "footer" })} className="text-xs text-kore-warm/60 hover:text-kore-light transition">Message on WhatsApp</a>
              </span>
            </li>
          </ul>
        </div>

        <div>
          <div className="eyebrow mb-5">Discover</div>
          <ul className="space-y-4 text-base text-kore-warm/80 leading-[1.6] font-light">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  data-testid={`footer-nav-${link.to.replace(/\//g, "").replace(/-/g, "_") || "home"}`}
                  className="min-h-[44px] inline-flex items-center hover:text-kore-light transition"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex gap-4">
            <a
              href="https://instagram.com/kore_wellbeing"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              data-testid="footer-instagram"
              className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-kore-warm/70 hover:text-kore-light transition"
            >
              <Instagram size={18} />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-kore-warm/10">
        <div className="container-kore py-6 flex flex-col md:flex-row items-center justify-between text-[13px] text-kore-warm/55 tracking-wide gap-3">
          <p>© {new Date().getFullYear()} Kore Wellbeing. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-kore-light transition">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-kore-light transition">Terms</Link>
            <Link to="/admin/login" data-testid="footer-admin" className="hover:text-kore-light transition">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
